import { Pagination } from '@/@types';
import configuration from '@/configuration';

export const gql = String.raw;

type FileEntry = File | Blob;

function extractFilesAndBuildFormData({ query, variables }: { query: string; variables: any }) {
  const files: { path: string; file: FileEntry }[] = [];
  // graphql specifications requires mutations to be a POST request
  if (query.includes('mutation ')) {
    return { method: 'post', body: JSON.stringify({ query, variables }), contentType: 'application/json' };
  }

  const clone = (obj: any, path: string): any => {
    if (obj instanceof File || obj instanceof Blob) {
      files.push({ path, file: obj });
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map((item, i) => {
        return clone(item, `${path}[${i}]`);
      });
    }

    if (obj && typeof obj === 'object') {
      return Object.entries(obj).reduce(
        (acc, [key, value]) => {
          acc[key] = clone(value, `${path}.${key}`);
          return acc;
        },
        {} as Record<string, any>,
      );
    }

    return obj;
  };

  const clonedVariables = clone(variables, 'variables');

  if (files.length === 0) {
    return {
      querystring: new URLSearchParams({ query, variables }).toString(),
      method: 'get',
    };
  }

  const formData = new FormData();
  formData.append('operations', JSON.stringify({ query, variables: clonedVariables }));

  const map: Record<string, string[]> = {};
  files.forEach(({ path }, idx) => {
    map[idx] = [path];
  });

  formData.append('map', JSON.stringify(map));
  files.forEach(({ file }, idx) => {
    formData.append(String(idx), file);
  });

  return { body: formData, method: 'post' };
}

export const getGraphlClient = ({ token, locale, source = 'web' }: { token?: string; locale?: string; source?: string }) => {
  const url = new URL(configuration.server?.graphql || configuration.graphql);
  if (locale) {
    url.searchParams.set('locale', locale);
  }

  return async <T = any>({ query, variables }: { query: ReturnType<typeof gql>; variables?: any }): Promise<{ data: T; errors?: any[] }> => {
    const { body, method, contentType } = extractFilesAndBuildFormData({ query, variables });
    if (method === 'get') {
      url.searchParams.set('query', query.replace(/\s+/g, ' '));
      url.searchParams.set('variables', JSON.stringify(variables || {}));
    }

    return fetch(url.toString(), {
      method,
      body,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(contentType && { 'content-type': contentType }),
        'x-request-source': source,
      },
    }).then((x) => {
      if (!x.ok) {
        console.log('ERROR', x.statusText);
      }
      return x.json();
    });
  };
};

export const paginationToArray = <T extends any>(pagination?: null | Pagination<T>): T[] => {
  if (!pagination) {
    return [];
  }
  return pagination.edges.map((x) => {
    return x.node;
  });
};
